import {
  type BillingSummary,
  getSummaryUsage,
  type BillingLicensePlans,
  getBillingLicensePlans,
  type BillingBusinessLicense,
  getBusinessLicense,
  type BillingPaymentMethods,
  getPaymentMethods,
  type BillingCreateSetupIntent,
  createSetupIntent,
  type BillingScheduledBusinessLicenses,
  getScheduledBusinessLicenses,
} from '@kizen/api/billing';
import type { DefaultError, QueryObserverOptions } from '@tanstack/query-core';
import type { AxiosInstance } from 'axios';
import type { Drop, Resource, WithRequired } from './types';
import { curry1, getResource } from './utils';

type BillingScheduledBusinessLicensesQuery<
  TData = BillingScheduledBusinessLicenses,
  TError = DefaultError,
> = WithRequired<
  QueryObserverOptions<
    BillingScheduledBusinessLicenses,
    TError,
    TData,
    BillingScheduledBusinessLicenses,
    [
      Resource<
        Readonly<['billing', 'business-license', 'scheduled-business-license']>
      >,
    ]
  >,
  'queryFn' | 'queryKey'
>;

export const getScheduledBusinessLicensesQuery = <
  TData = BillingScheduledBusinessLicenses,
>(
  axios: AxiosInstance,
  options?: Omit<
    BillingScheduledBusinessLicensesQuery<TData>,
    'queryFn' | 'queryKey'
  >
): BillingScheduledBusinessLicensesQuery<TData> => {
  const path = [
    'billing',
    'business-license',
    'scheduled-business-license',
  ] as const;
  const resource = getResource(path);

  return <BillingScheduledBusinessLicensesQuery<TData>>{
    queryKey: [resource],
    queryFn: (context) => {
      const { signal } = context;
      return getScheduledBusinessLicenses(axios, { signal });
    },
    ...options,
  };
};

type BillingCreateSetupIntentQuery<
  TData = BillingCreateSetupIntent,
  TError = DefaultError,
> = WithRequired<
  QueryObserverOptions<
    BillingCreateSetupIntent,
    TError,
    TData,
    BillingCreateSetupIntent,
    [Resource<Readonly<['billing', 'create-setup-intent']>>]
  >,
  'queryFn' | 'queryKey'
>;

export const createSetupIntentQuery = <TData = BillingCreateSetupIntent>(
  axios: AxiosInstance,
  options?: Omit<BillingCreateSetupIntentQuery<TData>, 'queryFn' | 'queryKey'>
): BillingCreateSetupIntentQuery<TData> => {
  const path = ['billing', 'create-setup-intent'] as const;
  const resource = getResource(path);

  return <BillingCreateSetupIntentQuery<TData>>{
    queryKey: [resource],
    queryFn: (context) => {
      const { signal } = context;
      return createSetupIntent(axios, { signal });
    },
    ...options,
  };
};

type BillingPaymentMethodsQuery<
  TData = BillingPaymentMethods,
  TError = DefaultError,
> = WithRequired<
  QueryObserverOptions<
    BillingPaymentMethods,
    TError,
    TData,
    BillingPaymentMethods,
    [Resource<Readonly<['billing', 'payment-methods']>>]
  >,
  'queryFn' | 'queryKey'
>;

export const getPaymentMethodsQuery = <TData = BillingPaymentMethods>(
  axios: AxiosInstance,
  options?: Omit<BillingPaymentMethodsQuery<TData>, 'queryFn' | 'queryKey'>
): BillingPaymentMethodsQuery<TData> => {
  const path = ['billing', 'payment-methods'] as const;
  const resource = getResource(path);

  return <BillingPaymentMethodsQuery<TData>>{
    queryKey: [resource],
    queryFn: (context) => {
      const { signal } = context;
      return getPaymentMethods(axios, { signal });
    },
    ...options,
  };
};

type BillingSummaryQuery<
  TData = BillingSummary,
  TError = DefaultError,
> = WithRequired<
  QueryObserverOptions<
    BillingSummary,
    TError,
    TData,
    BillingSummary,
    [Resource<Readonly<['billing', 'summary-usage']>>]
  >,
  'queryFn' | 'queryKey'
>;

export const getSummaryUsageQuery = <TData = BillingSummary>(
  axios: AxiosInstance,
  options?: Omit<BillingSummaryQuery<TData>, 'queryFn' | 'queryKey'>
): BillingSummaryQuery<TData> => {
  const path = ['billing', 'summary-usage'] as const;
  const resource = getResource(path);

  return <BillingSummaryQuery<TData>>{
    queryKey: [resource],
    queryFn: (context) => {
      const { signal } = context;
      return getSummaryUsage(axios, { signal });
    },
    ...options,
  };
};

type BillingLicensePlansQuery<
  TData = BillingLicensePlans,
  TError = DefaultError,
> = WithRequired<
  QueryObserverOptions<
    BillingLicensePlans,
    TError,
    TData,
    BillingLicensePlans,
    [Resource<Readonly<['billing', 'license-plans']>>]
  >,
  'queryFn' | 'queryKey'
>;

export const getBillingLicensePlansQuery = <TData = BillingLicensePlans>(
  axios: AxiosInstance,
  options?: Omit<BillingLicensePlansQuery<TData>, 'queryFn' | 'queryKey'>
): BillingLicensePlansQuery<TData> => {
  const path = ['billing', 'license-plans'] as const;
  const resource = getResource(path);

  return <BillingLicensePlansQuery<TData>>{
    queryKey: [resource],
    queryFn: (context) => {
      const { signal } = context;
      return getBillingLicensePlans(axios, { signal });
    },
    ...options,
  };
};

type BillingBusinessLicenseQuery<
  TData = BillingBusinessLicense,
  TError = DefaultError,
> = WithRequired<
  QueryObserverOptions<
    BillingBusinessLicense,
    TError,
    TData,
    BillingBusinessLicense,
    [Resource<Readonly<['billing', 'business-license']>>]
  >,
  'queryFn' | 'queryKey'
>;

export const getBusinessLicenseQuery = <TData = BillingBusinessLicense>(
  axios: AxiosInstance,
  options?: Omit<BillingBusinessLicenseQuery<TData>, 'queryFn' | 'queryKey'>
): BillingBusinessLicenseQuery<TData> => {
  const path = ['billing', 'business-license'] as const;
  const resource = getResource(path);

  return <BillingBusinessLicenseQuery<TData>>{
    queryKey: [resource],
    queryFn: (context) => {
      const { signal } = context;
      return getBusinessLicense(axios, { signal });
    },
    ...options,
  };
};

class Queries {
  public getSummaryUsageQuery: <TData = BillingSummary>(
    ...args: Drop<Parameters<typeof getSummaryUsageQuery<TData>>>
  ) => BillingSummaryQuery<TData>;

  public getBillingLicensePlansQuery: <TData = BillingLicensePlans>(
    ...args: Drop<Parameters<typeof getBillingLicensePlansQuery<TData>>>
  ) => BillingLicensePlansQuery<TData>;

  public getBusinessLicenseQuery: <TData = BillingBusinessLicense>(
    ...args: Drop<Parameters<typeof getBusinessLicenseQuery<TData>>>
  ) => BillingBusinessLicenseQuery<TData>;

  public getPaymentMethodsQuery: <TData = BillingPaymentMethods>(
    ...args: Drop<Parameters<typeof getPaymentMethodsQuery<TData>>>
  ) => BillingPaymentMethodsQuery<TData>;

  public createSetupIntentQuery: <TData = BillingCreateSetupIntent>(
    ...args: Drop<Parameters<typeof createSetupIntentQuery<TData>>>
  ) => BillingCreateSetupIntentQuery<TData>;

  public getScheduledBusinessLicensesQuery: <
    TData = BillingScheduledBusinessLicenses,
  >(
    ...args: Drop<Parameters<typeof getScheduledBusinessLicensesQuery<TData>>>
  ) => BillingScheduledBusinessLicensesQuery<TData>;

  constructor(axios: AxiosInstance) {
    this.getSummaryUsageQuery = curry1(getSummaryUsageQuery)(axios);
    this.getBillingLicensePlansQuery = curry1(getBillingLicensePlansQuery)(
      axios
    );
    this.getBusinessLicenseQuery = curry1(getBusinessLicenseQuery)(axios);
    this.getPaymentMethodsQuery = curry1(getPaymentMethodsQuery)(axios);
    this.createSetupIntentQuery = curry1(createSetupIntentQuery)(axios);
    this.getScheduledBusinessLicensesQuery = curry1(
      getScheduledBusinessLicensesQuery
    )(axios);
  }
}

export type BillingQueries = Queries;

export const defBillingQueries = (axios: AxiosInstance): BillingQueries => {
  return new Queries(axios);
};
